import { Component, inject, ChangeDetectionStrategy, DestroyRef, ChangeDetectorRef, signal, ViewChild, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AgGridModule } from "ag-grid-angular";
import { CellDoubleClickedEvent, GridApi, GridOptions, RowDoubleClickedEvent, ColDef, GridReadyEvent, ColTypeDef, ValueParserParams, SortChangedEvent, GridSizeChangedEvent, FirstDataRenderedEvent } from "ag-grid-community";
import { AgGridGroupDataSource } from "./ag-grid-group.datasource";
import { AgGridgroupService } from "./ag-grid-group.service";
import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged, filter, fromEvent, map, tap } from "rxjs";
import { AgGridGroupStore } from "./ag-grid-group.store";
import { FormControl } from "@angular/forms";
import { DEFAULT_CUSTOM_RENDERER_GROUP, MIN_LENGTH_TEXT_SEARCH } from ".";
import { STATUS_IN_DATABASE } from "app/shared/common/constants/Status.constant";
import { UtilComponent } from "app/shared/common/utils/UtilComponent";
import { IAgGridRowStatus } from "../ag-grid-without-cache/ag-grid-without-cache.model";
import { UtilCommon } from "app/shared/common/utils/UtilCommon";
import { TranslocoService } from "@ngneat/transloco";
import { Events } from "app/shared/common/utils/Events";
import { GroupRowInnerRenderer } from "./component/CustomCellRenderer";
import { APP_CONFIG } from "app.config";
import { ICustomRendererGroup } from "./ag-grid-group.interface";
import { AggridGroupHeaderComponent } from "./component/aggrid-group-header/aggrid-group-header.component";
import { AggridGroupCellComponent } from "./component/aggrid-group-cell/aggrid-group-cell.component";

@Component({
  selector: "app-ag-grid-group",
  standalone: true,
  imports: [CommonModule, AgGridModule],
  providers: [],
  templateUrl: "./ag-grid-group.component.html",
  styleUrl: "./ag-grid-group.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridGroupComponent {
  public subscriptions: Subscription[] = [];

  private dataSource: AgGridGroupDataSource = inject(AgGridGroupDataSource);
  public store: AgGridGroupStore = inject(AgGridGroupStore);
  protected service: AgGridgroupService = inject(AgGridgroupService);
  protected destroyRef = inject(DestroyRef);
  protected utilComponent: UtilComponent = inject(UtilComponent);
  protected translocoService: TranslocoService = inject(TranslocoService);
  protected cd: ChangeDetectorRef = inject(ChangeDetectorRef);

  protected gridAPI: GridApi = null;

  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef;
  protected searchControl = new FormControl("");
  protected pivotCountOnTitle$: Subject<string> = new Subject<string>();
  protected DEBOUNCE_SEARCH = 150;
  public rowData$: Observable<any[]> = this.store.data$.asObservable();
  public advancedFilterChanges$ = new Subject();
  public defaultColDef: {
    flex: 1;
    minWidth: 150;
    resizable: true,
  };
  public autoGroupColumnDef: {
    flex: 1;
    minWidth: 350;
  };
  protected ID_FIELD = "code";
  public btnUnActive = false;
  public btnActive = false;
  public btnConfig = false;
  public permissions: { [key: string]: boolean } = {
    update: true,
    create: true,
    delete: true,
    updateStatus: true,
  };
  public isUsingSelect = false;
  public isFirstEnter: boolean = true;

  public groupTotalRow: "top" | "bottom" = "bottom";
  protected customRendererGroup: ICustomRendererGroup = DEFAULT_CUSTOM_RENDERER_GROUP;
  public isActiveFilter = signal<boolean>(false);
  public columnTypes: {
    [key: string]: ColTypeDef;
  } = {
      number: {
        editable: true,
        // editing works with strings, need to change string to number
        valueParser: (params: ValueParserParams) => {
          return parseInt(params?.newValue);
        },
        aggFunc: "sum",
      },
    };

  public gridOptions: GridOptions = {
    context: {
      refreshTrigger: this.store.refreshTrigger$.asObservable()
    },
    rowModelType: "serverSide",
    autoSizeStrategy: {
      type: "fitCellContents", // column width with content. Then column width expand for grid width using "fitGridWidth"
    },
    components: {
      aggridGroupHeaderComponent: AggridGroupHeaderComponent,
      aggridGroupCellComponent: AggridGroupCellComponent,
    },
    sideBar: {
      toolPanels: [],
      defaultToolPanel: 'columns'
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    },

    suppressMovableColumns: true, // Keep columns non-movable
    suppressMenuHide: false,      // But still show menu button
    // Customize menu to show only specific items
    getMainMenuItems: (params) => {
      return [
        'pinSubMenu',        // Pin Column options
        'separator',
        'autoSizeThis',      // Autosize This Column
        'autoSizeAll',       // Autosize All Columns
        'separator',
        'resetColumns'       // Reset Columns
      ];
    },

    editType: 'fullRow',
    animateRows: false,
    autoGroupColumnDef: {
      suppressMovable: true,
      headerName: "", // headerName: "Group",
      minWidth: 300, // set the minimum width
      maxWidth: 650, // set the maximum width,
      flex: 1,
      headerComponent: 'aggridGroupHeaderComponent',
      cellRenderer: 'aggridGroupCellComponent',
      cellRendererParams: {
        suppressCount: true,
      }
    },
    rowHeight: APP_CONFIG.ROW_HEIGHT_40,
    rowSelection: "single",
    isRowSelectable: this.isRowSelectable,
    onRowDoubleClicked: this.rowDoubleClickedEvent.bind(this),
    onRowClicked: this.rowClickedEvent.bind(this),
    onCellDoubleClicked: this.cellDoubleClickedEvent.bind(this),
    onGridReady: this.onGridReady.bind(this),
    onFirstDataRendered: this.onFirstDataRendered.bind(this),
    onCellClicked: this.onCellClicked.bind(this),
    getContextMenuItems: this.getCustomContextMenuItems.bind(this),
    processCellForClipboard: this.processCellForClipboardValue.bind(this),
    pagination: true,
    // paginationPageSize: 10,
    // paginationAutoPageSize: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [50, 100, 200, 500, 1000, 2000],
    cacheBlockSize: 50,
    suppressAggFuncInHeader: true,
    onModelUpdated: () => {
      // this.gridAPI?.autoSizeAllColumns();
    },
    // onGridSizeChanged: (event: GridSizeChangedEvent) => {
    //   this.updateAttribute(event?.api);
    // },
    onSortChanged: (event: SortChangedEvent) => {
      this.updateAttribute(event?.api);
    },
    onColumnMoved: (event: any) => {
      this.updateAttribute(event?.api);
    },
    onColumnRowGroupChanged: (event: any) => {
      this.updateAttribute(event?.api);
    },
    rowMultiSelectWithClick: true,
    getChildCount: (data: any) => {
      return data?.recordCount > 0 ? data?.recordCount : null;
    },
    onRowGroupOpened: (event: any) => {
      this.store.setCurrentNodeOpenning(event?.data);
    },
    onColumnPinned: this.onColumnChanged.bind(this),
    onColumnResized: this.onColumnChanged.bind(this),
    onColumnVisible: this.onColumnChanged.bind(this),
    // onFirstDataRendered: (event: FirstDataRenderedEvent) => {
    //   // First, auto-size columns based on cell content
    //   event.api.autoSizeAllColumns();

    //   // Defer execution to ensure the grid DOM is fully rendered
    //   setTimeout(() => {
    //     // Calculate total width of all rendered columns
    //     const allColumns = event.api.getColumns();
    //     const totalColWidth =
    //       allColumns?.reduce((sum, col) => sum + (col.getActualWidth() || 0), 0) || 0;

    //     // Get the available width of the grid viewport
    //     const gridElement = document.querySelector('.ag-body-viewport') as HTMLElement;
    //     const gridWidth = gridElement?.clientWidth || 0;

    //     // If columns do not fill the grid width, stretch them to avoid white space
    //     if (totalColWidth < gridWidth) {
    //       event.api.sizeColumnsToFit();
    //     }
    //   }, 0);
    // },
    onGridSizeChanged: (event: GridSizeChangedEvent) => {
      // First, auto-size columns based on cell content
      event.api.autoSizeAllColumns();

      // Recalculate column and grid widths on container resize (e.g. screen scaling, window resize)
      const allColumns = event.api.getColumns();
      const totalColWidth =
        allColumns?.reduce((sum, col) => sum + (col.getActualWidth() || 0), 0) || 0;

      const gridWidth = event.clientWidth || 0;

      // Stretch columns only when there is remaining horizontal space
      if (totalColWidth < gridWidth) {
        event.api.sizeColumnsToFit();
      }

      this.updateAttribute(event?.api);
    },
  };

  public groupRowRendererParams: any = {
    innerRenderer: GroupRowInnerRenderer,
    suppressCount: true,
  };

  private _arraysAreEqual(arr1, arr2) {
    if (arr1?.length !== arr2?.length) {
      return false;
    }
    for (let i = 0; i < arr1?.length; i++) {
      if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
        return false;
      }
    }
    return true;
  }

  ngOnInit() {
    if (this.store.pageAttribute) {
      this.getUserAttribute();

    } else {
      this.initGridUI();

    }
    this.registerValueChanges();
    this.signalIsActiveFilter();
  }

  private getUserAttribute(): void {
    this.store
      .getUserAttribute()
      .then(() => {
        this.searchControl.setValue(this.store.searchKeyword);
        if (this.searchInput) {
          this.searchInput.nativeElement.value = this.store.searchKeyword;
        }
        this.initGridUI();
      })
      .catch((error) => {
        console.error("getUserAttribute", error);
      });
  }

  private _columnDefs: ColDef[] = [];

  get columnDefs() {
    return this._columnDefs;
  }

  set columnDefs(value: ColDef[]) {
    this._columnDefs = value;
  }

  protected buildColumnDefs(columnDefs: any = null) { }

  protected onGridReady(params: GridReadyEvent<any>) {
    this.gridAPI = params.api;
    params.api!.setGridOption("serverSideDatasource", this.dataSource);
    this.gridAPI?.closeToolPanel();
    // this.gridAPI?.autoSizeAllColumns();

    // Set Grouping when getUserAttribute from localStorage
    if (UtilCommon.isArray(this.store.groupState, true)) {
      this.selectionGroupChanges(this.store.groupState);
    }
  }

  protected onFirstDataRendered(event: FirstDataRenderedEvent) {
    // Set Grouping when getUserAttribute from query
    if (this.store.isQueryGetUserAttribute) {
      if (UtilCommon.isArray(this.store.groupState, true)) {
        this.selectionGroupChanges(this.store.groupState);
      }
    }

    // First, auto-size columns based on cell content
    event.api.autoSizeAllColumns();

    // Defer execution to ensure the grid DOM is fully rendered
    setTimeout(() => {
      // Calculate total width of all rendered columns
      const allColumns = event.api.getColumns();
      const totalColWidth =
        allColumns?.reduce((sum, col) => sum + (col.getActualWidth() || 0), 0) || 0;

      // Get the available width of the grid viewport
      const gridElement = document.querySelector('.ag-body-viewport') as HTMLElement;
      const gridWidth = gridElement?.clientWidth || 0;

      // If columns do not fill the grid width, stretch them to avoid white space
      if (totalColWidth < gridWidth) {
        event.api.sizeColumnsToFit();
      }
    }, 0);
  }

  protected cellDoubleClickedEvent(event: CellDoubleClickedEvent) { }

  protected rowDoubleClickedEvent(event: RowDoubleClickedEvent) { }

  protected rowClickedEvent(event: RowDoubleClickedEvent) {
    if (this.isUsingSelect) {
      this.gridAPI.refreshCells({
        columns: ['id', 'aggId'],  // Replace 'select' with the actual colId of your checkbox column
        force: true
      });
    }
  }

  public getDataSource() {
    return this.dataSource;
  }

  protected onCellClicked(event: any) {
    this.enableButtonActiveandUnactive(event.data);
    if (this.isUsingSelect) {
      // Check if the clicked cell is in the first column (for example, by colId or field)
      if (event.column.getColId() === 'id' || event.column.getColId() === 'aggId') {
        // Skip selection logic if clicked on 'select' column (e.g., checkbox column)
        return;
      }

      // Clear other selections for single select
      const selectedNodes = this.gridAPI.getSelectedNodes();
      selectedNodes.forEach(node => {
        if (node !== event.node) {
          node.setSelected(false, true);
        }
      });

      // Select the clicked row
      event.node.setSelected(true, true);
    }
  }

  isRowSelectable(node: any): boolean {
    return !node?.group || node?.leafGroup;
  }

  protected onColumnChanged(event: any) {
    if (this.store.pageAttribute) {
      const columnState = event?.api?.getColumnState();
      const isTheSame = this._arraysAreEqual(
        this.store.columnState,
        columnState
      );
      if (!isTheSame) {
        this.store.columnDefs = this.gridAPI?.getColumnDefs();
        this.store.columnState = columnState;
        this.store.setUserAttribute();
      }
    }
  }

  updateColumnDefs(event: any): ColDef[] {
    return this.gridAPI?.getColumnDefs()?.map((col: ColDef) => {
      this.updateColumnDef(col, event);
      return col;
    }) ?? [];
  }

  updateColumnDef(col: ColDef, event: any): void {
    event?.forEach((e) => {
      if (col.field == e?.field) {
        col.rowGroup = e?.rowGroup;
        col.enableRowGroup = e?.enableRowGroup;
      }
    });
  }

  public selectionGroupChanges(event: ColDef[]) {
    let newColDefs = this.updateColumnDefs(event);
    if (UtilCommon.isArray(newColDefs, true)) {
      this.gridAPI?.updateGridOptions({
        columnDefs: newColDefs
      });

      this.onColumnChanged({ api: this.gridAPI });
    }
  }

  public enableButtonActiveandUnactive(value: any) {
    this.handleDisableButtonUpdateRecordStatus();

    if (!value) return;

    if (
      value.recordStatus === STATUS_IN_DATABASE.ACTIVE &&
      value.flagDynamicForm === 1
    ) {
      this.btnConfig = true;
    }
    if (value.recordStatus === STATUS_IN_DATABASE.UNACTIVE) {
      this.btnActive = true;
    } else if (value.recordStatus === STATUS_IN_DATABASE.ACTIVE) {
      this.btnUnActive = true;
    }
    this.cd.markForCheck();
  }

  handleDisableButtonUpdateRecordStatus() {
    this.btnUnActive = false;
    this.btnActive = false;
    this.btnConfig = false;
  }

  async callUpdateRecordStatus(
    aggId: string,
    recordStat: any,
    eventTriggerOtherScreen?: string
  ): Promise<void> {
    try {
      this.store.setLoading(true);
      const data = {} as IAgGridRowStatus;
      data.aggId = aggId;
      data.recordStatus = recordStat;
      const dataSubmit = await this.service.updateRowStatus(
        data,
        this.dataSource.AGGRID_GROUP_API.UPDATE_RECORD_STATUS
      );
      this.store.setLoading(false);
      if (!UtilCommon.isNotNull(dataSubmit)) {
        this.utilComponent.openSnackBar(
          this.translocoService.translate(
            "SETTING.MESSAGE.UPDATE_STATUS.SUCCESS"
          ),
          "complete"
        );
        this.handleDisableButtonUpdateRecordStatus();
        this.gridAPI.deselectAll();
        if (eventTriggerOtherScreen) {
          Events.publish(eventTriggerOtherScreen);
        }
        return;
      }
      if (dataSubmit && dataSubmit.validateResult) {
        this.utilComponent.showTranslateSnackbar(
          this.translocoService.translate(
            "SETTING.MESSAGE.FORM_VALID_FAIL"
          ),
          "error"
        );
      } else if (dataSubmit && dataSubmit.errorcode) {
        if (dataSubmit.message) {
          this.utilComponent.openSnackBar(dataSubmit.message, "error");
        } else {
          this.utilComponent.showTranslateSnackbar(
            "GENERAL.ERROR_MESSAGE.SYSTEM_ERROR",
            "error"
          );
        }
      }
    } catch (error) {
      this.store.setLoading(false);
      // this.utilComponent.handleError(error);
      this.utilComponent.showTranslateSnackbar(
        "GENERAL.ERROR_MESSAGE.SYSTEM_ERROR",
        "error"
      );
    }
  }

  public updateRecordStatus(
    recordStat: any,
    eventTriggerOtherScreen?: string
  ): void {
    const selectedRowNodes = this.gridAPI?.getSelectedNodes();
    if (selectedRowNodes.length > 0) {
      this.utilComponent.openConfirmationDialog(
        this.translocoService.translate(
          "SETTING.MESSAGE.CONFIRM_UPDATE_RECORD_STATUS"
        ),
        (result: any) => {
          if (result === "confirmed") {
            const aggId = selectedRowNodes[0].data["aggId"];
            this.callUpdateRecordStatus(
              aggId,
              recordStat,
              eventTriggerOtherScreen
            );
          }
        }
      );
    }
  }

  protected registerValueChanges() {
    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(this.DEBOUNCE_SEARCH),
          distinctUntilChanged(),
          tap((query) => {
            this.queryFtsValue(query);
            // if (
            //   query?.trim()?.length > MIN_LENGTH_TEXT_SEARCH ||
            //   query?.length === 0
            // ) {
            //   this.store.setSearchKeyword(query);
            //   if (this.store.pageAttribute) {
            //     this.store.setUserAttribute();
            //   }
            //   this.gridAPI?.setFilterModel(null);
            //   if (!this.isFirstEnter || query?.length > 0) {
            //     this?.gridAPI?.deselectAll();
            //     this.gridAPI.refreshServerSide({
            //       purge: true
            //     });
            //   }
            //   this.isFirstEnter = false;
            // }
          })
        )
        .subscribe()
    );

    this.subscriptions.push(
      this.advancedFilterChanges$
        .pipe(
          tap((value: { filter: any, group: any, clear: boolean }) => {
            this.store.advancedFilter = value?.filter;
            this.store.groupState = value?.group;
            this.selectionGroupChanges(value?.group);

            // Add ftsValue
            const ftsValue = this?.searchInput?.nativeElement?.value;
            if (ftsValue) {
              this.store.setSearchKeyword(ftsValue);
            }

            if (this.store.pageAttribute) {
              this.store.setUserAttribute(true);
            }
            this.gridAPI?.refreshServerSide?.({
              purge: true
            });
            this.signalIsActiveFilter();
          })
        )
        .subscribe())

    this.subscriptions.push(
      this.store.count$.asObservable()
        .pipe(
          tap(() => this.cd.detectChanges()),
        )
        .subscribe())

    this.subscriptions.push(
      this.store.loading$.asObservable().subscribe(() => {
        this.cd.detectChanges();
      }))

    fromEvent(this.searchInput?.nativeElement, 'keydown')
      .pipe(
        filter((event: KeyboardEvent) => event.key === 'Enter'),
        map(() => this.searchInput.nativeElement.value)
      )
      .subscribe((searchTerm: string) => {
        this.queryFtsValue(searchTerm);
        // this.searchControl.setValue(searchTerm)
      });

    this.destroyRef.onDestroy(() => {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
      if (this?.store?.pageAttribute) {
        this?.store?.setUserAttribute(true);
      }
    });
  }

  queryFtsValue(query: string): void {
    if (query?.trim()?.length > MIN_LENGTH_TEXT_SEARCH
      || query?.length === 0) {
      this.store.setSearchKeyword(query);
      if (this.store.pageAttribute) {
        this.store.setUserAttribute();
      }
      this.gridAPI?.setFilterModel(null);
      if (!this.isFirstEnter || query?.length > 0) {
        this?.gridAPI?.deselectAll();
        this.gridAPI.refreshServerSide({
          purge: true
        });
      }
      this.isFirstEnter = false;
    }
  }

  signalIsActiveFilter(): void {
    let isActiveFilter = false;
    if (this.store.advancedFilter && Object.keys(this.store.advancedFilter).length > 0) {
      isActiveFilter = true;
    }
    if (UtilCommon.isArray(this.store.groupState, true) && this.store.groupState.some(item => item.rowGroup === true)) {
      isActiveFilter = true;
    }
    this.isActiveFilter.set(isActiveFilter);
  }

  protected refreshCache(route?: string[]) {
    this.store.setSearchKeyword("");
    this.searchControl.setValue(null, { onlySelf: true, emitEvent: false });
    if (this.searchInput) {
      this.searchInput.nativeElement.value = null;
    }
    this.gridAPI?.setFilterModel(null);
    this.gridAPI?.refreshServerSide({
      purge: true
    });
    this.btnUnActive = false;
    this.btnActive = false;
    this.gridAPI?.deselectAll();

    setTimeout(() => {
      if (this.store.pageAttribute) {
        this.store.resetUserAttribute();
      }
    }, 500);
  }

  private initGridUI(): void {
    this.buildColumnDefs(this.store.columnDefs);
    this.setUserAttributeToGrid();
  }

  private setUserAttributeToGrid(): void {
    const columnState = this.store.columnState;
    if (columnState) {
      let newColumnDef = [];
      for (let index = 0; index < columnState.length; index++) {
        const element = columnState[index];
        if (this.gridAPI) {
          this.gridAPI!.applyColumnState({
            state: [{
              ...element
            }],
          });
          for (
            let indexColumnDefs = 0;
            indexColumnDefs < this.gridAPI.getColumnDefs().length;
            indexColumnDefs++
          ) {
            const elementColumnDef: any =
              this.gridAPI.getColumnDefs()[indexColumnDefs];
            if (element.colId === elementColumnDef.colId) {
              newColumnDef.push(elementColumnDef);
            }
          }
        }
      }
      if (newColumnDef.length > 0) {
        this.columnDefs = newColumnDef;
      }
    }
  }

  private updateAttribute(gridApi: any) {
    if (this.store.pageAttribute) {
      const columnState = gridApi?.getColumnState();
      const isTheSame = this._arraysAreEqual(
        this.store.columnState,
        columnState
      );
      if (!isTheSame) {
        this.store.columnState = columnState;
        this.store.setUserAttribute();
      }
    }
  }

  public getCustomContextMenuItems(params: any): Array<any> {
    // const colId = params.column.getColId().toLowerCase();
    // // Show context menu only for 'mobile', 'phone', 'personalEmail', and 'email' columns
    // if (colId.includes('mobile') || colId.includes('phone') || colId.includes('email')) {
    //   return [
    //     'copy',  // Built-in Copy option
    //     'paste'  // Built-in Paste option (requires ClipboardModule)
    //   ];
    // }
    // // Return an empty array for other columns to hide the context menu
    // return [];

    return [
      {
        name: 'Copy Cell Value',
        icon: '<span class="ag-icon ag-icon-copy"></span>',
        action: () => this.copyToClipboard(params) // Copy the exact cell value
      },
      // {
      //   name: 'Open Details',
      //   icon: '<span class="ag-icon ag-icon-expanded"></span>',
      //   action: () => {
      //     // Create a fake RowDoubleClickedEvent
      //     const fakeEvent: RowDoubleClickedEvent<any, any> = {
      //       node: params.node,
      //       data: params.node.data,
      //       rowIndex: params.node.rowIndex,
      //       rowPinned: params.node.rowPinned,
      //       api: params.api,
      //       columnApi: params.columnApi,
      //       context: params.context,
      //       type: 'rowDoubleClicked',
      //     };

      //     this.rowDoubleClickedEvent(fakeEvent);
      //   },
      // },
      // 'separator',
      // 'copy',  // Built-in Copy option
      // 'paste'  // Built-in Paste option (requires ClipboardModule)
    ];
  }

  public processCellForClipboardValue(params: any): any {
    let value = params?.value;

    const colId = params?.column?.getColId()?.toLowerCase();
    if (colId) {
      if ((colId.includes('mobile') || colId.includes('phone')) && value) {
        return UtilCommon.formatPhoneNumber(value);
      }
      if ((colId.endsWith('date') || colId === 'dob') && value) {
        return UtilCommon.formatDateFullTime(value);
      }
      if (colId.includes('status') && value) {
        return UtilCommon.capitalizeFirstCharacterOfWords(value);
      }
      if (colId.includes('name') && value) {
        let result = value;
        if (value?.value && value?.type === 'jsonb') {
          result = UtilCommon.parseJson(value.value);
        }
        return UtilCommon.getLabelLanguage(result);
      }
    }
    if (value?.type === 'jsonb' && value?.value) {
      const parsed = UtilCommon.parseJson(value.value);
      return UtilCommon.getLabelLanguage(parsed);
    }
    if (value && typeof value === 'object') {
      let result = UtilCommon.getLabelLanguage(value?.label || value?.name || value) || value;
      if (value?.email) {
        result = value.email + ' - ' + result;
      }
      return result;
    }

    // When value is undefined/null, try to resolve it via the column definition
    if (value == null) {
      const colDef = params?.column?.getColDef();
      const field = colDef?.field;

      // 1. Attempt to read directly from row data using the column's field
      if (field && params?.node?.data) {
        value = params.node.data[field];
      }

      // 2. Fall back to valueFormatter if still unresolved
      if (value == null && typeof colDef?.valueFormatter === 'function') {
        value = colDef.valueFormatter({ ...params, value });
      }

      // 3. Fall back to cellRenderer if it is a plain function (not a component string/class)
      if (value == null && typeof colDef?.cellRenderer === 'function') {
        const rendered = colDef.cellRenderer({
          ...params,
          data: params?.node?.data,
          value: field ? params?.node?.data?.[field] : undefined,
        });
        if (typeof rendered === 'number') {
          value = rendered;
        } else if (typeof rendered === 'string') {
          // Strip any HTML markup returned by the renderer
          const tempEl = document.createElement('div');
          tempEl.innerHTML = rendered;
          const text = tempEl.textContent || tempEl.innerText || '';
          value = text.trim() || null;
        }
      }
    }

    return value ?? null; // Default behavior for other columns
  }

  private copyToClipboard(params: any): void {
    const value = this.processCellForClipboardValue(params);
    if (value !== null && value !== undefined) {
      navigator.clipboard.writeText(value.toString()).then(() => {
        // console.log('Copied:', value);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }
}
